import { useState, type ReactNode } from "react";
import { Modal } from "@/components/Modal";
import { AlertTriangle } from "lucide-react";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  title: string;
  /** Free-form body content above the typed confirmation input. */
  body: ReactNode;
  /** Word the user must type to enable the confirm button. */
  confirmWord: string;
  /** Label of the destructive action button. */
  confirmLabel: string;
  /** Icon color + button style: "danger" (red), "warning" (amber), "primary" (blue). */
  variant?: "danger" | "warning" | "primary";
};

const BTN_CLASS: Record<NonNullable<Props["variant"]>, string> = {
  danger: "btn btn-danger btn-sm",
  warning: "btn btn-sm bg-warning-500 hover:bg-warning-600 text-white",
  primary: "btn btn-primary btn-sm",
};

const ICON_CLASS: Record<NonNullable<Props["variant"]>, string> = {
  danger: "text-danger-700 bg-danger-50",
  warning: "text-warning-700 bg-warning-50",
  primary: "text-brand-700 bg-brand-50",
};

export function TypedConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmWord,
  confirmLabel,
  variant = "danger",
}: Props) {
  const [typed, setTyped] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const matches = typed.trim().toLowerCase() === confirmWord.toLowerCase();

  const reset = () => {
    setTyped("");
    setLoading(false);
    setError(null);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    if (!matches || loading) return;
    setLoading(true);
    setError(null);
    try {
      await onConfirm();
      reset();
      onClose();
    } catch (err: any) {
      setError(err?.detail || err?.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size="md"
      closeOnOverlay={!loading}
      footer={
        <>
          <button type="button" onClick={handleClose} disabled={loading} className="btn btn-light btn-sm">
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!matches || loading}
            className={BTN_CLASS[variant]}
          >
            {loading ? "Traitement…" : confirmLabel}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${ICON_CLASS[variant]}`}>
            <AlertTriangle size={20} />
          </div>
          <div className="text-sm text-[#3f4254] leading-relaxed">{body}</div>
        </div>

        <div>
          <label className="form-label">
            Pour confirmer, tapez <code className="bg-[#f5f8fa] px-1.5 py-0.5 rounded font-mono text-xs">{confirmWord}</code>
          </label>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="form-input"
            placeholder={confirmWord}
            autoFocus
          />
        </div>

        {error && <div className="bg-danger-50 text-danger-700 text-sm rounded-lg px-4 py-3">{error}</div>}
      </div>
    </Modal>
  );
}
